import { Link } from 'react-router-dom';
import {
  FiShield, FiCamera, FiUsers, FiHeadphones, FiTool, FiSearch,
  FiZap, FiCheckCircle, FiArrowRight, FiRadio, FiEye, FiStar,
  FiAlertOctagon, FiActivity,
} from 'react-icons/fi';
import {
  MdOutlineEmojiPeople, MdOutlineElectricalServices,
} from 'react-icons/md';
import { BsCarFront, BsGearFill } from 'react-icons/bs';
import { TbBulb } from 'react-icons/tb';

const securityServices = [
  'Corporate Security', 'Residential Security', 'Escort Security',
  'Event Security', 'Personal Bodyguard', 'Night Patrolling',
  'Executive Protection', 'Static Guarding', 'Mobile Patrol', 'Investigation',
];

const recruitmentRoles = [
  'Security Officers', 'Skilled & Unskilled Labour', 'Drivers & Plant Operators',
  'Electrical Experts', 'Civil Experts', 'Plumbing Artisans',
  'Welding Artisans', 'Receptionists', 'Carpenters',
  'Mechanical Experts', 'ITC Experts',
];

const digitalServices = [
  {
    Icon: FiCamera,
    title: 'CCTV Installation & Maintenance',
    points: [
      'IP & analog cameras, NVR/DVR, storage sizing, and networking',
      'Remote viewing setup and user training',
      'Service-level agreements for uptime and support',
      'Compliance with data handling and evidence preservation',
    ],
  },
  {
    Icon: FiZap,
    title: 'Electric Fence Installation',
    points: [
      'Design and layout per site conditions',
      'Energizers, earthing, and safety signage',
      'Integration with CCTV and alarm systems',
      'Routine inspection and preventive maintenance',
    ],
  },
  {
    Icon: BsGearFill,
    title: 'Gate Motor Automation',
    points: [
      'Supply and installation of gate motors',
      'Access control: keypads, tags, biometrics, and safety sensors',
      'Emergency release and backup power options',
      'Periodic servicing and repairs',
    ],
  },
];

const equipment = [
  { Icon: FiRadio,                   label: 'Walkie Talkie' },
  { Icon: FiEye,                     label: 'Heavy Duty Torch' },
  { Icon: MdOutlineEmojiPeople,      label: 'Reflective Vest' },
  { Icon: FiStar,                    label: 'Bulletproof Vest' },
  { Icon: FiAlertOctagon,            label: 'Shotgun' },
  { Icon: FiSearch,                  label: 'Metal Detector' },
  { Icon: BsCarFront,                label: 'Supervision Vehicle' },
  { Icon: FiActivity,                label: 'Belts & Uniform' },
];

const casualServices = [
  'Event Setup & Support',
  'Loading & Offloading Assistance',
  'Cleaning & Maintenance',
  'Minor Construction & Repairs',
];

export default function Services() {
  return (
    <div className="animate-fadeUp">

      {/* ── HERO ── */}
      <section className="relative pt-40 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-navy-DEFAULT via-navy-mid to-navy-light" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(201,168,76,0.10),transparent_60%)]" />
        <div className="relative max-w-7xl mx-auto px-6">
          <span className="section-tag">Our Services</span>
          <h1 className="section-title mt-2 mb-4">
            Integrated <span className="text-gold">Security & Workforce</span> Solutions
          </h1>
          <div className="gold-divider" />
          <p className="text-gray-300 text-lg max-w-2xl leading-relaxed">
            From physical guarding and digital systems to skilled manpower and customer service agents —
            a comprehensive portfolio designed to meet every client's security and operational needs.
          </p>
        </div>
        <div className="absolute bottom-0 inset-x-0 h-24 bg-gradient-to-t from-navy-DEFAULT to-transparent" />
      </section>

      {/* ── 1. SECURITY SERVICES ── */}
      <section className="py-20 max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          <div>
            <span className="section-tag">01</span>
            <h2 className="section-title mb-4">Security <span className="text-gold">Services</span></h2>
            <div className="gold-divider" />
            <p className="text-gray-400 leading-relaxed mb-6">
              Our uniformed security personnel are background-checked, trained in access control, patrol routines,
              incident management, and emergency response — providing round-the-clock protection.
            </p>
            <div className="flex gap-4 flex-wrap items-center">
              <Link to="/contact" className="btn-primary">
                Get a Quote <FiArrowRight size={15} />
              </Link>
              <div className="flex items-center gap-2 text-gold text-sm font-medium">
                <FiCheckCircle size={15} /> Quality Guaranteed
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {securityServices.map(s => (
              <div key={s} className="flex items-center gap-3 bg-navy-card rounded-lg px-4 py-3 transition-all">
                <span className="w-1.5 h-1.5 rounded-full bg-gold flex-shrink-0" />
                <span className="text-gray-300 text-sm">{s}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 2. DIGITAL SECURITY ── */}
      <section className="py-20 bg-navy-mid">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="section-tag">02</span>
            <h2 className="section-title">Digital <span className="text-gold">Security Systems</span></h2>
            <div className="gold-divider mx-auto" />
            <p className="text-gray-400 max-w-xl mx-auto mt-2">
              Supply, installation, and maintenance of modern security technologies to complement physical guarding.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {digitalServices.map(({ Icon, title, points }) => (
              <div key={title} className="bg-navy-card rounded-2xl p-6 hover:-translate-y-1 transition-all duration-300">
                <div className="w-12 h-12 rounded-lg bg-gold/10 flex items-center justify-center mb-4">
                  <Icon className="text-gold" size={22} />
                </div>
                <h3 className="text-white font-semibold text-lg mb-4">{title}</h3>
                <ul className="space-y-2.5">
                  {points.map(p => (
                    <li key={p} className="flex items-start gap-2 text-gray-400 text-sm">
                      <FiArrowRight className="text-gold mt-0.5 flex-shrink-0" size={13} />
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 3. RECRUITMENT ── */}
      <section className="py-20 max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          <div className="grid grid-cols-2 gap-3 order-2 lg:order-1">
            {recruitmentRoles.map(r => (
              <div key={r} className="flex items-center gap-3 bg-navy-card rounded-lg px-4 py-3 transition-all">
                <span className="w-1.5 h-1.5 rounded-full bg-gold flex-shrink-0" />
                <span className="text-gray-300 text-sm">{r}</span>
              </div>
            ))}
          </div>
          <div className="order-1 lg:order-2">
            <span className="section-tag">03</span>
            <h2 className="section-title mb-4">Recruitment <span className="text-gold">Agency</span></h2>
            <div className="gold-divider" />
            <p className="text-gray-400 leading-relaxed mb-6">
              We match clients with vetted, qualified candidates across security, technical, and administrative
              roles. All placements are supported with background verification and induction training.
            </p>
            <div className="flex gap-4 flex-wrap items-center">
              <Link to="/contact" className="btn-primary">
                Request Staff <FiArrowRight size={15} />
              </Link>
              <div className="flex items-center gap-2 text-gold text-sm font-medium">
                <FiCheckCircle size={15} /> Free Quotation
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 4. CASUAL LABOUR ── */}
      <section className="py-20 bg-navy-mid">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="section-tag">04</span>
              <h2 className="section-title mb-4">Casual <span className="text-gold">Labour Services</span></h2>
              <div className="gold-divider" />
              <p className="text-gray-400 leading-relaxed mb-6">
                Verified and trustworthy workers for short-term and event-based tasks. Our workers undergo
                basic training in discipline and safety to ensure efficiency and reliability.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                {casualServices.map(s => (
                  <div key={s} className="flex items-center gap-3 bg-navy-card rounded-lg px-4 py-3">
                    <FiCheckCircle className="text-gold flex-shrink-0" size={15} />
                    <span className="text-gray-300 text-sm">{s}</span>
                  </div>
                ))}
              </div>
              <div className="bg-navy-card rounded-xl p-4 text-sm text-gray-400 flex items-start gap-3">
                <TbBulb className="text-gold flex-shrink-0 mt-0.5" size={18} />
                <span><span className="text-gold font-semibold">Pro tip:</span> Combine labour services with security for complete event or site safety.</span>
              </div>
            </div>
            <div className="bg-navy-card rounded-2xl p-8">
              <h3 className="text-white font-semibold text-lg mb-6">Why Choose Our Labour Services?</h3>
              <ul className="space-y-4">
                {[
                  [FiSearch,      'Background-verified workers for every assignment'],
                  [MdOutlineElectricalServices, 'Basic safety and discipline training included'],
                  [FiZap,         'Rapid mobilisation for urgent requirements'],
                  [FiUsers,       'Option to bundle with security for complete coverage'],
                  [FiCheckCircle, 'Daily/task-based or weekly engagement rates'],
                ].map(([Icon, text], i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-400 text-sm">
                    <Icon className="text-gold flex-shrink-0 mt-0.5" size={17} />
                    {text}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── 5. CUSTOMER SERVICE AGENTS ── */}
      <section className="py-20 max-w-7xl mx-auto px-6">
        <div className="text-center mb-14">
          <span className="section-tag">05</span>
          <h2 className="section-title">Customer Service <span className="text-gold">Agents</span></h2>
          <div className="gold-divider mx-auto" />
          <p className="text-gray-400 max-w-xl mx-auto mt-2">
            Professional agents for front desk, event ushering, and temporary call-centre support.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { Icon: FiHeadphones, title: 'Bilingual Agents',  desc: 'English & Swahili communication for diverse client bases.' },
            { Icon: FiUsers,      title: 'Brand Aligned',     desc: 'Grooming, etiquette, and uniform standards matching your brand.' },
            { Icon: FiTool,       title: 'CRM Ready',         desc: 'Familiarisation with basic CRM and ticketing systems.' },
            { Icon: FiShield,     title: 'Performance Tracked', desc: 'Daily summaries and KPI monitoring for accountability.' },
          ].map(({ Icon, title, desc }) => (
            <div key={title} className="card text-center group">
              <div className="w-12 h-12 rounded-full bg-gold/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-gold/20 transition-colors">
                <Icon className="text-gold" size={20} />
              </div>
              <h4 className="text-white font-semibold mb-2">{title}</h4>
              <p className="text-gray-400 text-sm leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── EQUIPMENT ── */}
      <section className="py-20 bg-navy-mid">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="section-tag">Equipment</span>
            <h2 className="section-title">Our <span className="text-gold">Equipment Schedule</span></h2>
            <div className="gold-divider mx-auto" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {equipment.map(({ Icon, label }) => (
              <div key={label} className="bg-navy-card rounded-xl p-5 flex flex-col items-center gap-3 hover:-translate-y-1 transition-all duration-300">
                <Icon className="text-gold" size={28} />
                <div className="text-gray-300 text-sm font-medium text-center">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TRAINING & COMPLIANCE ── */}
      <section className="py-20 max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-10">
          <div className="bg-navy-card rounded-2xl p-8 transition-all">
            <div className="w-12 h-12 rounded-lg bg-gold/10 flex items-center justify-center mb-5">
              <FiShield className="text-gold" size={22} />
            </div>
            <h3 className="text-white font-display text-xl font-bold mb-3">Training, Vetting &amp; Quality</h3>
            <div className="gold-divider" />
            <ul className="space-y-3 text-gray-400 text-sm">
              {[
                'Background checks and induction training for all personnel',
                'Guard training: access control, patrol routines, incident management',
                'Technician training: installation standards, testing & commissioning',
                'Service agent training: communication, complaint handling, escalation',
                'Supervision and audits with documented KPIs',
              ].map(item => (
                <li key={item} className="flex items-start gap-2">
                  <FiArrowRight className="text-gold mt-0.5 flex-shrink-0" size={13} />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-navy-card rounded-2xl p-8 transition-all">
            <div className="w-12 h-12 rounded-lg bg-gold/10 flex items-center justify-center mb-5">
              <FiCheckCircle className="text-gold" size={22} />
            </div>
            <h3 className="text-white font-display text-xl font-bold mb-3">Health, Safety &amp; Compliance</h3>
            <div className="gold-divider" />
            <ul className="space-y-3 text-gray-400 text-sm">
              {[
                'Adherence to Tanzanian regulations and industry best practices',
                'Site-specific risk assessments and safe work procedures',
                'PPE provision and enforcement at all sites',
                'Emergency preparedness: fire, medical, and evacuation',
                'Incident reporting and corrective action processes',
                'Client induction and toolbox talks',
              ].map(item => (
                <li key={item} className="flex items-start gap-2">
                  <FiArrowRight className="text-gold mt-0.5 flex-shrink-0" size={13} />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ── ENGAGEMENT MODELS ── */}
      <section className="py-20 bg-navy-mid">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="section-tag">Pricing</span>
            <h2 className="section-title">Engagement Models <span className="text-gold">&amp; Pricing</span></h2>
            <div className="gold-divider mx-auto" />
            <p className="text-gray-400 max-w-xl mx-auto mt-2">
              Flexible arrangements to suit every client. A detailed quotation is provided after a free site assessment.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { Icon: FiCheckCircle, title: 'Monthly Packages',     desc: 'Guarding contracts with included supervision and reporting.' },
              { Icon: FiTool,        title: 'Project-Based',         desc: 'Technology installations billed per project with warranty.' },
              { Icon: FiZap,         title: 'Daily / Task Rates',    desc: 'Casual and skilled labour on flexible daily or task rates.' },
              { Icon: FiShield,      title: 'Maintenance Contracts', desc: 'Optional SLAs, maintenance contracts, and rapid-response support.' },
            ].map(({ Icon, title, desc }) => (
              <div key={title} className="bg-navy-card rounded-xl p-6 hover:-translate-y-1 transition-all duration-300">
                <Icon className="text-gold mb-3" size={24} />
                <h4 className="text-white font-semibold mb-2">{title}</h4>
                <p className="text-gray-400 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="relative rounded-2xl overflow-hidden bg-navy-card p-10 md:p-16 text-center">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(201,168,76,0.07),transparent_70%)]" />
            <div className="relative">
              <h2 className="section-title mb-4">
                Request a <span className="text-gold">Free Site Assessment</span>
              </h2>
              <p className="text-gray-400 max-w-lg mx-auto mb-8">
                Tell us about your requirements and our team will prepare a detailed, no-obligation quotation.
              </p>
              <Link to="/contact" className="btn-primary">
                Contact Us Today <FiArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
